// src/locales/en/couriers.js
export const couriers = {
  // Business Couriers
  pageTitle: "Business Couriers",
  pageDescription: "Manage courier company assignments and configurations",
  assignCourier: "Assign Courier",
  noCouriersAssigned: "No couriers assigned yet",
  noCouriersFound: "No couriers found",
  startByAssigning: "Start by assigning courier companies to your business",
  searchCouriers: "Search couriers...",
  assignFirstCourier: "Assign First Courier",
  clearSearch: "Clear Search",
  loadingCouriers: "Loading couriers...",
  loadingMessage: "Please wait while we load your courier assignments",
  unableToLoad: "Unable to Load Couriers",
  retry: "Retry",
  noMatchingCouriers: "No couriers match your search criteria",
  courier: "Courier",
  status: "Status",
  
  // Stats
  totalAssigned: "Total Assigned",
  active: "Active",
  apiEnabled: "API Enabled",
  
  // Badges
  activeStatus: "Active",
  inactiveStatus: "Inactive",
  apiEnabledBadge: "API Enabled",
  webhookEnabledBadge: "Webhook Enabled",
  
  // Actions
  configure: "Configure",
  remove: "Remove",
  save: "Save Configuration",
  cancel: "Cancel",
  close: "Close",
  saving: "Saving...",
  
  // Assign Modal
  assignModalTitle: "Assign Courier Companies",
  assignModalDescription: "Select one or more courier companies to assign to your business.",
  noCouriersAvailable: "No courier companies available",
  assign: "Assign",
  assigning: "Assigning...",
  
  // Configuration Modal
  configureModalTitle: "Configure",
  generalSettings: "General Settings",
  activeStatusLabel: "Active Status",
  activeStatusHint: "Enable or disable this courier assignment",
  
  // API Configuration
  apiConfiguration: "API Configuration",
  enableApiIntegration: "Enable API Integration",
  enableApiHint: "Enable API integration for automated waybill generation",
  apiKey: "API Key",
  apiKeyPlaceholder: "Enter API key",
  apiKeyHint: "API key provided by the courier company",
  enableApiUpload: "Upload Orders via API",
  enableApiUploadHint: "When disabled, orders are not auto-uploaded to the courier; airwaybill numbers are generated manually from the waybill range instead. Tracking (getPackageDetails) keeps working as long as API integration is enabled.",
  apiEndpoint: "API Endpoint",
  apiEndpointPlaceholder: "https://api.courier.com/v1",
  apiEndpointHint: "Custom API endpoint URL (optional)",
  additionalApiConfig: "Additional API Configuration",
  additionalApiConfigPlaceholder: '{"merchantId": "12345", "senderName": "Your Business"}',
  additionalApiConfigHint: "Additional configuration parameters in JSON format",
  
  // Waybill Configuration
  waybillConfiguration: "Waybill Range Configuration",
  waybillDescription: "Configure manual waybill number range for this courier",
  waybillRangeStart: "Waybill Range Start",
  waybillRangeStartPlaceholder: "e.g., AWB000001",
  waybillRangeEnd: "Waybill Range End",
  waybillRangeEndPlaceholder: "e.g., AWB999999",
  lastWaybillUsed: "Last Waybill Used",
  waybillRange: "Waybill Range",
  lastUsed: "Last Used",
  
  // Webhook Configuration
  webhookConfiguration: "Webhook Configuration",
  enableWebhook: "Enable Webhook",
  enableWebhookHint: "Enable webhook to receive real-time courier status updates",
  webhookUrl: "Webhook URL",
  webhookUrlHint: "Share this URL with the courier company to receive status updates",
  regenerateWebhook: "Regenerate Webhook URL",
  regenerating: "Regenerating...",
  
  // Delete Modal
  confirmRemoval: "Confirm Removal",
  removeConfirmation: "Are you sure you want to remove",
  removeWarning: "This action will remove the courier assignment and all associated configurations.",
  removeCourier: "Remove Courier",
  removing: "Removing...",
  cannotRemove: "Cannot remove",
  hasRelatedRecords: "because it has related records:",
  removeRelatedRecordsFirst: "Please remove or reassign these records before deleting this courier account.",
  packingBatches: "packing batch(es)",
  orders: "order(s)",
  assignmentRulesCount: "assignment rule(s)",
  
  // Account Name
  accountName: "Account Name",
  accountNamePlaceholder: "e.g., Main Account",
  accountNameHint: "Display name for this courier account",
  accountNameRequired: "Account name is required",

  // Validation Errors
  invalidUrl: "Please enter a valid URL",
  invalidJson: "Invalid JSON format",
  rangeError: "End range cannot be less than start range",
  apiKeyRequired: "API key is required when API configuration is enabled",
  
  // Success Messages
  assignSuccess: "Successfully assigned courier(s)",
  createSuccess: "Successfully created {count} courier account(s)",
  updateSuccess: "Courier configuration updated successfully",
  removeSuccess: "Courier removed successfully",
  webhookEnabledSuccess: "Webhook enabled successfully",
  webhookDisabledSuccess: "Webhook disabled successfully",
  webhookRegeneratedSuccess: "Webhook secret regenerated successfully",
  
  // Error Messages
  assignError: "Failed to assign courier companies",
  updateError: "Failed to update courier configuration",
  removeError: "Failed to remove courier",
  loadError: "Failed to load business couriers",
  loadAvailableError: "Failed to load available courier companies",
  noChanges: "No changes to save",
  selectAtLeastOne: "Please select at least one courier company",
  webhookEnableError: "Failed to enable webhook",
  webhookDisableError: "Failed to disable webhook",
  webhookRegenerateError: "Failed to regenerate webhook secret",
  
  // Card Details
  addedOn: "Added",
  customEndpoint: "Custom Endpoint",
  
  // Contact Info
  phone: "Phone",
  email: "Email",
  website: "Website",
  address: "Address",

  // Column Mapping Overrides
  mappings: {
    modalTitle: "Column Mappings",
    modalDescription: "Custom overrides for this courier account. These take priority over company defaults.",
    openMappings: "Column Mappings",
    loadError: "Failed to load mapping overrides",
    invoiceType: "Courier Invoice Mapping",
    uploadType: "Upload Format Mapping",
    noOverride: "Using company default mapping",
    createOverride: "Create Override",
    edit: "Edit",
    delete: "Delete",
    save: "Save Mapping",
    mappingName: "Mapping Name",
    mappingNamePlaceholder: "e.g., My Custom Invoice Format",
    mappingConfig: "Mapping Configuration (JSON)",
    mappingConfigHint: "Full JSON mappingConfig object (see seeder for structure reference)",
    notes: "Notes",
    notesPlaceholder: "Optional developer notes...",
    activeStatus: "Active",
    nameRequired: "Mapping name is required",
    invalidJson: "Invalid JSON — please check the format",
    configMustBeObject: "Mapping config must be a JSON object",
    createSuccess: "Mapping override created",
    updateSuccess: "Mapping override updated",
    deleteSuccess: "Mapping override deleted",
    saveError: "Failed to save mapping override",
    deleteError: "Failed to delete mapping override",
    deleteConfirm: "Delete this mapping override? The courier will revert to the company default."
  },

  // Assignment Rules
  assignmentRules: "Courier Assignment Rules",
  assignmentRulesDescription: "Define and manage courier assignment rules based on geographic and shop criteria",
  createRule: "Create Rule",
  createFirstRule: "Create First Rule",
  editRule: "Edit Assignment Rule",
  createRuleTitle: "Create Assignment Rule",
  noRulesYet: "No assignment rules yet",
  noRulesDescription: "Start by creating courier assignment rules to automate courier selection",
  searchRules: "Search rules...",
  noRulesFound: "No rules found",
  noMatchingRules: "No rules match your search and filter criteria",
  loadingRules: "Loading assignment rules...",
  loadingRulesMessage: "Please wait while we load your configuration",
  unableToLoadRules: "Unable to Load Rules",
  
  // Rule Stats
  totalRules: "Total Rules",
  couriersConfigured: "Couriers Configured",
  defaultRules: "Default Rules",
  defaultFirst: "Default First",
  hasDefault: "Has Default",
  
  // Rule Form
  basicInformation: "Basic Information",
  courierCompany: "Courier Company",
  selectCourierCompany: "Select courier company",
  ruleName: "Rule Name",
  ruleNamePlaceholder: "e.g., Karachi Main Areas",
  priority: "Priority",
  priorityHint: "Higher numbers = higher priority",
  description: "Description",
  descriptionPlaceholder: "Optional description of this rule...",
  ruleConfiguration: "Rule Configuration",
  ruleConfigDescription: "Configure include/exclude rules. Rules are evaluated in order: Shop → City → Zone → District → Province",
  setAsDefault: "Set as Default Rule",
  setAsDefaultHint: "Only one rule per courier can be default",
  unsetAsDefault: "Unset as default",
  
  // Rule Types
  shopRules: "Shop Rules",
  cityRules: "City Rules",
  shippingZoneRules: "Shipping Zone Rules",
  districtRules: "District Rules",
  provinceRules: "Province Rules",
  
  // Rule Actions
  addRule: "Add Rule",
  noRulesConfigured: "No rules configured",
  include: "Include",
  exclude: "Exclude",
  showDetails: "Show Details",
  hideDetails: "Hide Details",
  duplicate: "Duplicate",
  copy: "Copy",
  
  // Rule Selection
  selectMultipleHint: "Hold Ctrl/Cmd to select multiple items",
  selected: "Selected",
  shop: "Shop",
  city: "City",
  zone: "Zone",
  district: "District",
  province: "Province",
  
  // Filters
  allCouriers: "All Couriers",
  allStatus: "All Status",
  sortBy: "Sort By",
  sortByCourier: "Courier",
  sortByPriority: "Priority",
  sortByStatus: "Status",
  sortByDate: "Date Created",
  clearFilters: "Clear Filters",
  
  // Delete Rule
  confirmDeletion: "Confirm Deletion",
  deleteRuleConfirmation: "Are you sure you want to delete the rule",
  deleteRuleWarning: "This action cannot be undone. The rule will be permanently removed.",
  deleteRule: "Delete Rule",
  deleting: "Deleting...",
  
  // Rule Success Messages
  ruleCreatedSuccess: "Assignment rule created successfully",
  ruleUpdatedSuccess: "Assignment rule updated successfully",
  ruleDeletedSuccess: "Assignment rule deleted successfully",
  ruleActivatedSuccess: "Rule activated successfully",
  ruleDeactivatedSuccess: "Rule deactivated successfully",
  defaultSetSuccess: "Default rule set successfully",
  defaultUnsetSuccess: "Default rule unset successfully",
  
  // Rule Error Messages
  ruleCreateError: "Failed to create assignment rule",
  ruleUpdateError: "Failed to update assignment rule",
  ruleDeleteError: "Failed to delete assignment rule",
  ruleLoadError: "Failed to load assignment rules data",
  ruleToggleError: "Failed to toggle rule status",
  defaultToggleError: "Failed to toggle default status",
  
  // Rule Details
  configuration: "Configuration",
  createdBy: "Created by",
  updatedBy: "Updated by",
  rules: "rules",
  rule: "rule",
  updateRule: "Update Rule",
  selectItemsToInclude: "Select items to include",
  selectItemsToExclude: "Select items to exclude",
  unknown: "Unknown",
  // Courier delivery charges (rate card)
  deliveryCharges: "Delivery Charges",
  deliveryChargesModalTitle: "Delivery Charges -",
  deliveryChargesModalDesc: "Set what this courier charges you for each shipping zone. Used to check courier invoices.",
  extraChargesDesc: "Courier charge per extra kilogram beyond the base delivery",
  deliveryChargesLoading: "Loading delivery charges...",
  saveDeliveryCharges: "Save Delivery Charges",
  deliveryChargesSaved: "Courier delivery charges saved successfully",
  deliveryChargesSaveFailed: "Failed to save courier delivery charges",
  deliveryChargesLoadFailed: "Failed to load courier delivery charges",
  deliveryChargesTab: "Delivery Charges",
  returnChargesTab: "Return Charges",
  returnChargesModalDesc: "Set what this courier charges you for returned orders in each shipping zone.",
  returnExtraChargesDesc: "Courier return charge per extra kilogram",
  setFreeReturnAll: "Free Returns for All",
  saveCharges: "Save Charges",
  bulkEditReturnCharges: "Bulk Edit Return Charges",
  returnChargeAmount: "Return Charge Amount",
  bulkEditReturnHint: "This amount will be applied to all shipping zones for returns",
  extraChargePerKgLabel: "Extra Charge per Kg",
  bulkEditExtraHint: "Leave empty to keep each zone's current extra charge"
};